import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import { useBakerActionsEnabled } from '../../hooks/useBakerActionsEnabled';
import { useRole } from '../../hooks/useRole';
import { getHalfMatchSummary, isQueuedHalf, moveOrderInList } from '../../utils/orderUtils';
import BakingTimerBanner from './BakingTimerBanner';
import QueueToolbar from './QueueToolbar';
import OrderCard from './OrderCard';

function BakerQueue() {
  const { t } = useTranslation();
  const enabled = useBakerActionsEnabled();
  const role = useRole();
  const orders = useAppStore((state) => state.orders);
  const showDoneOrders = useAppStore((state) => state.showDoneOrders);
  const reorderOrders = useAppStore((state) => state.reorderOrders);

  const visibleOrders = useMemo(() => {
    return orders.filter((order) => showDoneOrders || order.state !== 'served');
  }, [orders, showDoneOrders]);

  const visibleOrderIds = useMemo(() => visibleOrders.map((o) => o.id), [visibleOrders]);

  const bakingOrder = useMemo(
    () => orders.find((o) => o.state === 'baking') ?? null,
    [orders],
  );

  const queuedHalves = useMemo(
    () => visibleOrders.filter(isQueuedHalf),
    [visibleOrders],
  );

  const smartJoin = useMemo(() => {
    // Exactly two queued halves: direct join links replace the join list.
    if (queuedHalves.length === 2) return null;
    const topHalf = queuedHalves[0] ?? null;
    if (!topHalf) return null;

    const matches = queuedHalves
      .slice(1)
      .map((order) => ({ order, ...getHalfMatchSummary(topHalf, order) }))
      .sort((first, second) => second.common - first.common || first.additional - second.additional);

    return { orderId: topHalf.id, matches };
  }, [queuedHalves]);

  const joinWith = useMemo(() => {
    if (queuedHalves.length !== 2) return null;
    return {
      [queuedHalves[0].id]: queuedHalves[1],
      [queuedHalves[1].id]: queuedHalves[0],
    };
  }, [queuedHalves]);

  const handleMoveOrder = async (orderId, direction) => {
    const reordered = moveOrderInList(orders, orderId, direction, visibleOrderIds);
    if (reordered === orders) return;
    await reorderOrders(reordered.map((o) => o.id));
  };

  return (
    <section className="space-y-0">
      <BakingTimerBanner order={bakingOrder} />
      {!enabled && (
        <p className="mx-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm theme-accent-warm sm:mx-6">
          {role === 'anonymous' ? t('baker.lockedSession') : t('baker.locked')}
        </p>
      )}
      <QueueToolbar />

      <div className="px-4 py-4 sm:px-6">
        {visibleOrders.length === 0 ? (
          <p className="theme-muted">
            {orders.length === 0 ? t('baker.noOrders') : t('baker.noMatch')}
          </p>
        ) : (
          <div className="space-y-3">
            {visibleOrders.map((order, index) => (
              <OrderCard
                key={order.id}
                order={order}
                index={index}
                count={visibleOrders.length}
                onMove={handleMoveOrder}
                halfCount={queuedHalves.length}
                joinWith={joinWith?.[order.id]}
                smartJoin={smartJoin?.orderId === order.id ? smartJoin.matches : undefined}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default BakerQueue;